import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';

import { Version } from '../common/contracts';
import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';

export default function useDistributionManager() {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { publisherId } = useParams();

  const getVersions = useQuery<Version[]>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.DISTRIBUTION_MANAGER, publisherId],
    queryFn: async () => {
      const versions = await axios.get(
        `${getApiRouteOrMock(EApiRoutes.DISTRIBUTION_MANAGER)}/versions?limit=100`,
        {},
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
      return versions;
    },
    enabled: !!publisherId
  });

  const getVersionsCount = useQuery<Version[]>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.DISTRIBUTION_MANAGER, publisherId],
    queryFn: async () => {
      const versions = await axios.get(
        `${getApiRouteOrMock(EApiRoutes.DISTRIBUTION_MANAGER)}/versions/count`,
        {},
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
      return versions;
    },
    enabled: !!publisherId
  });

  const createVersion = useMutation({
    mutationFn: async (version: Partial<Version>) => {
      const response = await axios.put(
        `${getApiRouteOrMock(EApiRoutes.DISTRIBUTION_MANAGER)}/${version.versionCode}`,
        version,
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
      queryClient.invalidateQueries({
        queryKey: [EQueryKeys.DISTRIBUTION_MANAGER, publisherId]
      });
      return response.data;
    }
  });

  const getUploadUrl = async (versionCode: string, packageName: string) => {
    const response = await axios.get(
      `${getApiRouteOrMock(EApiRoutes.DISTRIBUTION_MANAGER)}/${versionCode}/upload-url?packageName=${packageName}`,
      {},
      publisherId ? { 'x-publishers': publisherId } : undefined
    );
    return response;
  };

  const uploadFileToSignedUrl = useMutation<
    unknown,
    Error,
    {
      file: File;
      signedUrl: string;
      onUploadProgress: AxiosRequestConfig['onUploadProgress'];
    }
  >({
    mutationFn: async ({ file, signedUrl, onUploadProgress }) => {
      const response = await axios.put(
        signedUrl,
        file,
        undefined,
        onUploadProgress
      );
      return response.data;
    }
  });

  return {
    getVersions,
    getUploadUrl,
    createVersion,
    getVersionsCount,
    uploadFileToSignedUrl
  };
}
